import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "../../context/ApiContext";

export default function RoomTypeForm({ mode = "create" }) {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id;
  const isEdit = mode === "edit" && id;
  const { adminGetRoomType, adminCreateRoomType, adminUpdateRoomType } = useApi();

  const [form, setForm] = useState({ name: "", beds: 1, baths: 1, sqft: 30, basePrice: 0, description: "", charges: [] });
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      if (!isEdit) return;
      try {
        setLoading(true);
        const data = await adminGetRoomType(id);
        if (active && data) {
          const normalized = {
            name: data.name || "",
            beds: Number(data.beds || 0),
            baths: Number(data.baths || 0),
            sqft: Number(data.sqft || 0),
            basePrice: Number(data.basePrice || 0),
            description: data.description || "",
            charges: Array.isArray(data.charges) ? data.charges : [],
          };
          setForm(normalized);
        }
      } catch (e) {
        setError(e?.message || "Failed to load room type");
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [isEdit, id, adminGetRoomType]);

  function onChange(e) {
    const { name, value } = e.target;
    const isNumeric = name === "basePrice" || name === "beds" || name === "baths" || name === "sqft";
    // Allow empty input while typing; coerce on save
    setForm((f) => ({
      ...f,
      [name]: isNumeric ? (value === "" ? "" : value) : value,
    }));
  }

  async function onSave(e) {
    e.preventDefault();
    if (!form.name.trim()) return alert("Name is required");

    const toNum = (v) => (v === "" || v === null || typeof v === "undefined" ? 0 : Number(v));
    const normalized = {
      ...form,
      basePrice: toNum(form.basePrice),
      beds: toNum(form.beds),
      baths: toNum(form.baths),
      sqft: toNum(form.sqft),
    };

    try {
      if (isEdit) {
        await adminUpdateRoomType(id, normalized);
      } else {
        await adminCreateRoomType(normalized);
      }
      navigate("/admin/room-types");
    } catch (e) {
      alert(e?.message || "Failed to save room type");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{isEdit ? "Edit Room Type" : "New Room Type"}</h1>
        <button onClick={() => navigate(-1)} className="px-3 py-2 rounded-md border">Back</button>
      </div>

      <div className="bg-white border rounded-lg p-4 max-w-3xl">
        {loading && <div className="text-gray-600">Loading...</div>}
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        <form onSubmit={onSave} className="space-y-3">
          <Field label="Name">
            <input name="name" value={form.name} onChange={onChange} className="w-full border rounded-md px-3 py-2" required />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Beds">
              <select name="beds" value={form.beds} onChange={onChange} className="w-full border rounded-md px-3 py-2">
                {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Field>
            <Field label="Baths">
              <input type="number" name="baths" value={form.baths} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
            </Field>
            <Field label="Sqm">
              <input type="number" name="sqft" value={form.sqft} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
            </Field>
          </div>

          <Field label="Base Price (ZAR)">
            <input type="number" name="basePrice" value={form.basePrice} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
          </Field>

          <ChargesEditor
            charges={Array.isArray(form.charges) ? form.charges : []}
            onChange={(next) => setForm((f) => ({ ...f, charges: next }))}
          />

          <Field label="Description">
            <textarea name="description" rows={3} value={form.description} onChange={onChange} className="w-full border rounded-md px-3 py-2" />
          </Field>

          <div className="flex gap-2">
            <button type="submit" className="px-3 py-2 rounded-md bg-black text-white">Save</button>
            <button type="button" onClick={() => navigate("/admin/room-types")} className="px-3 py-2 rounded-md border">Cancel</button>
          </div>
        </form>
        <p className="text-xs text-gray-500 mt-3">Room types are stored on the server and used to pre-fill new units.</p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ChargesEditor({ charges, onChange }) {
  function addRow() {
    onChange([...(charges || []), { id: rid(), name: "", amount: 0 }]);
  }
  function updateRow(id, patch) {
    onChange((charges || []).map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }
  function removeRow(id) {
    onChange((charges || []).filter((c) => c.id !== id));
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-gray-700">Charges</label>
        <button type="button" onClick={addRow} className="px-2 py-1 border rounded-md text-sm">Add</button>
      </div>
      {(charges || []).length === 0 ? (
        <div className="text-xs text-gray-500">No extra charges.</div>
      ) : (
        <div className="space-y-2">
          {(charges || []).map((c) => (
            <div key={c.id} className="grid grid-cols-7 gap-2 items-center">
              <input
                className="col-span-4 border rounded-md px-3 py-2 text-sm"
                placeholder="Charge name (e.g., Deposit)"
                value={c.name}
                onChange={(e) => updateRow(c.id, { name: e.target.value })}
              />
              <input
                type="number"
                className="col-span-2 border rounded-md px-3 py-2 text-sm"
                placeholder="Amount"
                value={c.amount}
                onChange={(e) => updateRow(c.id, { amount: Number(e.target.value || 0) })}
              />
              <button type="button" onClick={() => removeRow(c.id)} className="px-2 py-2 border rounded-md text-sm">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function rid() {
  // simple client-only id for charge rows
  return `chg_${Math.random().toString(36).slice(2, 10)}`;
}
